import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PasswordInput } from "@weco/ui";
import { isWorkspacePausedBody } from "@weco/suite-client";
import { peopleAuthUrl } from "../config/peopleApi";
import { useShellStore } from "../store";
import { applyLocale } from "../i18n/applyLocale";
import { markWorkspacePaused, useWorkspacePausedNotice } from "../lib/billing/workspacePausedNotice";

const ShieldIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const ServerIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
    <line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/>
  </svg>
);
const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

/**
 * LoginScreen — We-Co Suite auth screen
 *
 * Left panel: branding + value props
 * Right panel: email/password login form
 */
const inputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
  (e.currentTarget as HTMLElement).style.borderColor = "var(--accent)";
  (e.currentTarget as HTMLElement).style.boxShadow = "0 0 0 3px rgba(var(--accent-rgb),0.15)";
};
const inputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
  (e.currentTarget as HTMLElement).style.borderColor = "var(--border)";
  (e.currentTarget as HTMLElement).style.boxShadow = "none";
};

/**
 * Honor `?next=` from satellite SPA round-trips (e.g. video lobby → login → back).
 * Same-origin paths only: must start with `/` and not `//`. Cross-origin or
 * malformed values fall through to the existing Zustand-driven re-render.
 */
const honorNextParam = (): void => {
  if (typeof window === "undefined") return;
  const next = new URLSearchParams(window.location.search).get("next");
  if (!next) return;
  if (!next.startsWith("/") || next.startsWith("//")) return;
  window.location.assign(next);
};

const LoginScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { setUser } = useShellStore();
  const locale: "en" | "ar" = i18n.language === "ar" ? "ar" : "en";
  // The topbar's EN/ع pill only exists in the authenticated shell — the login
  // page needs its own toggle or an Arabic user can never reach Arabic.
  const toggleLocale = () => {
    const next: "en" | "ar" = locale === "en" ? "ar" : "en";
    localStorage.setItem("weco-locale", next);
    void i18n.changeLanguage(next);
    applyLocale(next);
  };
  const [mode, setMode] = useState<"signin" | "forgot" | "mfa" | "password_change">("signin");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [forgotSent, setForgotSent] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  // "email": the code was mailed to mfaSentTo (masked) and can be resent.
  const [mfaMethod, setMfaMethod] = useState<"totp" | "email">("totp");
  const [mfaSentTo, setMfaSentTo] = useState<string | null>(null);
  const [mfaNotice, setMfaNotice] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const [useRecovery, setUseRecovery] = useState(false);
  const [changeToken, setChangeToken] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [retrySecondsLeft, setRetrySecondsLeft] = React.useState<number | null>(null);
  // Set when People refuses this person because their workspace is paused for
  // an unpaid bill — at sign-in, the 2FA step, the forced password change, or
  // a session renewal that signed them out (see lib/billing).
  const workspacePaused = useWorkspacePausedNotice();

  /** 403 workspace_paused at any sign-in step: explain it, back to step one. */
  const handlePausedRefusal = (status: number, data: unknown): boolean => {
    if (status !== 403 || !isWorkspacePausedBody(data)) return false;
    markWorkspacePaused();
    setError(null);
    setPassword("");
    setMfaToken(null);
    setMfaCode("");
    setChangeToken(null);
    setNewPassword("");
    setConfirmPassword("");
    setMode("signin");
    return true;
  };

  React.useEffect(() => {
    if (!retrySecondsLeft) return;
    const id = setInterval(
      () => setRetrySecondsLeft((s) => (s !== null && s > 1 ? s - 1 : null)),
      1000,
    );
    return () => clearInterval(id);
  }, [retrySecondsLeft]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const loginUrl = peopleAuthUrl("login/");
      const res = await fetch(loginUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // credentials: "include" makes the browser accept the backend's
        // Set-Cookie for the `.we-co.io`-scoped access cookie + HttpOnly
        // refresh cookie when people-backend is on a different subdomain
        // (staging: VITE_PEOPLE_API_URL → cross-origin). The new Set-Cookie
        // overwrites any stale cookie left by a previous user (same name +
        // domain + path), which is what GH-776 AC-1/AC-3 depend on.
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json().catch(() => null);

      if (handlePausedRefusal(res.status, data)) return;

      if (res.status === 429) {
        const secs: number = data?.retry_after_seconds ?? 900;
        setRetrySecondsLeft(secs);
        setError(data?.detail ?? t("login.errTooManyLogins"));
        return;
      }

      if (!res.ok) {
        if (res.status >= 500) {
          throw new Error(t("login.errPeopleUnavailable"));
        }
        const detail = data?.detail;
        const msg =
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail.map((d: { string?: string }) => d?.string || JSON.stringify(d)).join(" ")
              : detail
                ? JSON.stringify(detail)
                : t("login.errInvalidCredentials");
        throw new Error(msg);
      }
      if (data?.password_change_required && data?.change_token) {
        setChangeToken(data.change_token);
        setNewPassword("");
        setConfirmPassword("");
        setPassword("");
        setMode("password_change");
        return;
      }
      // Two-factor: the password step succeeded but we need a code before the
      // backend issues tokens. Switch to the second step instead of signing in.
      if (data?.mfa_required && data?.mfa_token) {
        startMfaStep(data);
        setPassword("");
        return;
      }
      setUser(data.user, data.access, data.refresh);
      honorNextParam();
    } catch (err: any) {
      if (err?.name === "TypeError" && String(err?.message).includes("fetch")) {
        setError(t("login.errPeopleUnavailable"));
      } else {
        setError(err.message || t("login.errLoginFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setTimeout(() => setResendIn((v) => Math.max(0, v - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [resendIn]);

  /** Password step passed: show the second-factor prompt for the user's method. */
  const startMfaStep = (data: {
    mfa_token: string;
    mfa_method?: string;
    mfa_sent_to?: string | null;
    mfa_resend_after?: number;
    mfa_code_sent?: boolean;
  }) => {
    const byEmail = data.mfa_method === "email";
    setMfaToken(data.mfa_token);
    setMfaCode("");
    setUseRecovery(false);
    setMfaMethod(byEmail ? "email" : "totp");
    setMfaSentTo(byEmail ? (data.mfa_sent_to ?? null) : null);
    setMfaNotice(byEmail && data.mfa_code_sent === false ? t("login.errMfaEmailSend") : null);
    setResendIn(byEmail ? Number(data.mfa_resend_after) || 0 : 0);
    setMode("mfa");
  };

  const handleMfaResend = async () => {
    if (!mfaToken || resendIn > 0) return;
    setError(null);
    setMfaNotice(null);
    try {
      const res = await fetch(peopleAuthUrl("login/mfa/resend/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ mfa_token: mfaToken }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok) {
        if (data?.sent_to) setMfaSentTo(data.sent_to);
        setResendIn(Number(data?.resend_after) || 60);
        setMfaCode("");
        setMfaNotice(t("login.mfaEmailResent"));
      } else if (res.status === 429) {
        setResendIn(Number(data?.retry_after) || 60);
      } else if (res.status === 400) {
        // The challenge expired or was used: start again.
        setError(data?.detail ?? t("login.errVerificationFailed"));
      } else {
        setError(t("login.errMfaEmailSend"));
      }
    } catch {
      setError(t("login.errMfaEmailSend"));
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await fetch(peopleAuthUrl("login/mfa/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ mfa_token: mfaToken, code: mfaCode.trim() }),
      });
      const data = await res.json().catch(() => null);

      if (handlePausedRefusal(res.status, data)) return;
      if (res.status === 429) {
        setError(t("login.errTooManyAttempts"));
        setMode("signin");
        setMfaToken(null);
        return;
      }
      if (res.status === 401 && !data?.access) {
        // Bad code, or the challenge expired.
        setError(data?.detail ?? t("login.errBadCode"));
        return;
      }
      if (!res.ok) {
        throw new Error(data?.detail ?? t("login.errVerificationFailed"));
      }
      setUser(data.user, data.access, data.refresh);
      honorNextParam();
    } catch (err: any) {
      setError(err?.message || t("login.errVerificationFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChangeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError(t("login.errPasswordMismatch"));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(peopleAuthUrl("login/password-change/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ change_token: changeToken, new_password: newPassword }),
      });
      const data = await res.json().catch(() => null);

      if (handlePausedRefusal(res.status, data)) return;
      if (res.status === 429) {
        setError(t("login.errTooManyAttempts"));
        setMode("signin");
        setChangeToken(null);
        setNewPassword("");
        setConfirmPassword("");
        return;
      }
      if (!res.ok) {
        const detail = data?.detail;
        const errorsArr = Array.isArray(data?.errors) ? data.errors : null;
        const msg =
          typeof detail === "string"
            ? detail
            : errorsArr && errorsArr.length && typeof errorsArr[0]?.detail === "string"
              ? errorsArr[0].detail
              : Array.isArray(detail)
                ? detail.map((d: { string?: string }) => d?.string || JSON.stringify(d)).join(" ")
                : t("login.errPasswordChangeFailed");
        throw new Error(msg);
      }
      // The backend re-checks MFA after completing the forced change (Fix 1) —
      // an enrolled user gets a challenge here instead of tokens, exactly
      // like the normal sign-in path.
      if (data?.mfa_required && data?.mfa_token) {
        startMfaStep(data);
        return;
      }
      setUser(data.user, data.access, data.refresh);
      honorNextParam();
    } catch (err: any) {
      setError(err?.message || t("login.errPasswordChangeFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const url = peopleAuthUrl("password-reset/");
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (res.status === 429) {
          const detail = data?.detail;
          throw new Error(typeof detail === "string" ? detail : t("login.errTooManyRequests"));
        }
        const detail = data?.detail;
        const msg =
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail.map((d: { string?: string }) => d?.string || JSON.stringify(d)).join(" ")
              : detail
                ? JSON.stringify(detail)
                : t("login.errResetFailed");
        throw new Error(msg);
      }

      setForgotSent(true);
    } catch (err: unknown) {
      const anyErr = err as { name?: string; message?: string };
      if (anyErr?.name === "TypeError" && String(anyErr?.message).includes("fetch")) {
        setError(
          "Cannot reach the People API. Start backends (bash scripts/run-backend.sh) and use the shell on the active Vite URL."
        );
      } else {
        setError(anyErr.message || t("login.errResetFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: <ShieldIcon />, title: t("login.featureSecureTitle"), desc: t("login.featureSecureDesc") },
    { icon: <ServerIcon />, title: t("login.featureSelfHostedTitle"), desc: t("login.featureSelfHostedDesc") },
    { icon: <LockIcon />, title: t("login.featureAccessTitle"), desc: t("login.featureAccessDesc") },
  ];

  return (
    <div
      className="relative flex h-screen overflow-hidden"
      style={{ background: "var(--bg-root)", fontFamily: "Inter, sans-serif" }}
    >
      {/* Language toggle — same labels as the topbar's locale pill */}
      <button
        type="button"
        onClick={toggleLocale}
        aria-label={locale === "en" ? t("locale.toArabic") : t("locale.toEnglish")}
        title={locale === "en" ? t("locale.toArabic") : t("locale.toEnglish")}
        className="absolute top-5 end-5 z-20 px-2.5 rounded-full text-xs font-bold tracking-[0.06em]"
        style={{
          height: "28px",
          background: "var(--bg-surface-2)",
          border: "1px solid var(--border)",
          color: "var(--text-secondary)",
        }}
      >
        {locale === "en" ? "ع" : "EN"}
      </button>

      {/* ── Left panel — branding ──────────────────────────────────────── */}
      <div
        className="hidden lg:flex flex-col justify-between w-[45%] p-12 relative overflow-hidden"
        style={{ borderRight: "1px solid var(--border)" }}
      >
        {/* Background grid */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `
              linear-gradient(var(--border) 1px, transparent 1px),
              linear-gradient(90deg, var(--border) 1px, transparent 1px)
            `,
            backgroundSize: "48px 48px",
          }}
        />

        {/* Glow orb */}
        <div
          className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full opacity-10 pointer-events-none"
          style={{ background: "radial-gradient(circle, #3366FF 0%, transparent 70%)" }}
        />

        {/* Logo */}
        <div className="relative flex items-center gap-3 z-10">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg"
            style={{ background: "#3366FF", color: "#FFFFFF" }}
          >
            W
          </div>
          <div>
            <span className="inline-flex items-baseline gap-1.5">
              <span className="text-xl font-bold" style={{ color: "var(--text-primary)" }}>We-Co Suite</span>
              <span
                className="text-[10px] font-bold tracking-widest uppercase"
                style={{ color: "var(--accent)" }}
              >
                {t("shell.betaLabel")}
              </span>
            </span>
            <p className="text-[10px] font-medium tracking-widest uppercase mt-0.5" style={{ color: "var(--text-muted)" }}>
              {t("login.brandTagline")}
            </p>
          </div>
        </div>

        {/* Main copy */}
        <div className="relative z-10">
          <div className="mb-6">
            <p
              className="text-[10px] font-bold tracking-widest uppercase mb-4"
              style={{ color: "var(--accent)" }}
            >
              {t("login.brandKicker")}
            </p>
            <h1
              className="text-4xl font-black leading-tight mb-4"
              style={{ color: "var(--text-primary)", letterSpacing: "-0.03em" }}
            >
              {t("login.heroLine1")}<br />
              <span
                style={{
                  background: "linear-gradient(135deg, #3366FF 0%, #598EFF 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {t("login.heroLine2")}
              </span>
            </h1>
            <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              {t("login.heroBody")}
            </p>
          </div>

          {/* Feature list */}
          <div className="space-y-4">
            {features.map(f => (
              <div key={f.title} className="flex items-start gap-3">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                  style={{
                    background: "var(--accent-dim)",
                    border: "1px solid var(--accent-border)",
                    color: "var(--accent)",
                  }}
                >
                  {f.icon}
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{f.title}</p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom status */}
        <div className="relative z-10 flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500" style={{ boxShadow: "0 0 4px #22C55E" }} />
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
            {t("login.systemsOperational")}
          </span>
        </div>
      </div>

      {/* ── Right panel — sign in ──────────────────────────────────────── */}
      <div className="flex flex-col items-center justify-center flex-1 px-8">

        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-3 mb-10">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg"
            style={{ background: "#3366FF", color: "#FFFFFF" }}
          >
            W
          </div>
          <span className="inline-flex items-baseline gap-1.5">
            <span className="text-xl font-bold" style={{ color: "var(--text-primary)" }}>We-Co Suite</span>
            <span
              className="text-[10px] font-bold tracking-widest uppercase"
              style={{ color: "var(--accent)" }}
            >
              {t("shell.betaLabel")}
            </span>
          </span>
        </div>

        <div className="w-full max-w-sm animate-fade-in">

          {/* Heading */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--text-primary)" }}>
              {forgotSent
                ? t("login.headingCheckEmail")
                : mode === "password_change"
                  ? t("login.headingPasswordChange")
                  : mode === "mfa"
                    ? t("login.headingMfa")
                    : mode === "signin"
                      ? t("login.headingSignIn")
                      : t("login.headingForgot")}
            </h2>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {forgotSent
                ? t("login.subCheckEmail")
                : mode === "password_change"
                  ? t("login.subPasswordChange")
                  : mode === "mfa"
                    ? useRecovery
                      ? t("login.subMfaRecovery")
                      : mfaMethod === "email"
                        ? mfaSentTo
                          ? t("login.subMfaEmail", { email: mfaSentTo })
                          : t("login.subMfaEmailNoAddress")
                        : t("login.subMfaCode")
                    : mode === "signin"
                      ? t("login.subSignIn")
                      : t("login.subForgot")}
            </p>
          </div>

          {workspacePaused && !forgotSent && (
            <div
              role="alert"
              data-testid="workspace-paused-notice"
              className="text-sm px-3 py-3 rounded-lg mb-4"
              style={{
                background: "rgba(245,158,11,0.12)",
                color: "var(--text-primary)",
                border: "1px solid rgba(245,158,11,0.35)",
              }}
            >
              {t("login.workspacePaused")}
            </div>
          )}

          {forgotSent ? (
            <div className="space-y-4 mb-4">
              <p className="text-sm px-3 py-3 rounded-lg" style={{ background: "var(--bg-surface-2)", color: "var(--text-secondary)" }}>
                {t("login.checkEmailBody")}
              </p>
              <button
                type="button"
                onClick={() => {
                  setForgotSent(false);
                  setMode("signin");
                  setError(null);
                }}
                className="w-full py-3 rounded-xl text-sm font-bold transition-all duration-200"
                style={{
                  background: "#3366FF",
                  color: "#FFFFFF",
                  border: "1px solid transparent",
                  boxShadow: "0 8px 32px rgba(var(--accent-rgb),0.3)",
                }}
              >
                {t("login.backToSignIn")}
              </button>
            </div>
          ) : mode === "mfa" ? (
            <form onSubmit={handleMfaSubmit} className="space-y-4 mb-4">
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
                  {useRecovery
                    ? t("login.recoveryCodeLabel")
                    : mfaMethod === "email"
                      ? t("login.emailCodeLabel")
                      : t("login.authCodeLabel")}
                </label>
                <input
                  type="text"
                  inputMode={useRecovery ? "text" : "numeric"}
                  autoFocus
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder={useRecovery ? "XXXX-XXXX" : "123456"}
                  required
                  className="w-full px-3 py-2.5 rounded-xl text-sm outline-none transition-all duration-150 tracking-widest"
                  style={{
                    background: "var(--bg-surface-2)",
                    border: "1px solid var(--border)",
                    color: "var(--text-primary)",
                  }}
                  onFocus={inputFocus}
                  onBlur={inputBlur}
                />
              </div>

              {error && (
                <p className="text-xs px-3 py-2 rounded-lg" style={{ background: "rgba(239,68,68,0.1)", color: "#EF4444" }}>
                  {error}
                </p>
              )}

              {mfaMethod === "email" && !useRecovery && (
                <div className="flex items-center justify-between gap-2 text-xs" style={{ color: "var(--text-secondary)" }}>
                  <span role="status">{mfaNotice ?? t("login.mfaEmailNoCode")}</span>
                  <button
                    type="button"
                    onClick={() => void handleMfaResend()}
                    disabled={resendIn > 0}
                    data-testid="mfa-resend"
                    className="font-medium shrink-0 disabled:opacity-60"
                    style={{ color: resendIn > 0 ? "var(--text-secondary)" : "var(--accent)" }}
                  >
                    {resendIn > 0 ? t("login.resendCodeIn", { count: resendIn }) : t("login.resendCode")}
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-3 rounded-xl text-sm font-bold transition-all duration-200"
                style={{
                  background: loading ? "var(--bg-surface-3)" : "#3366FF",
                  color: loading ? "var(--text-secondary)" : "#FFFFFF",
                  border: "1px solid transparent",
                  boxShadow: loading ? "none" : "0 8px 32px rgba(var(--accent-rgb),0.3)",
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? t("login.verifying") : t("login.verify")}
              </button>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setUseRecovery((v) => !v);
                    setMfaCode("");
                    setError(null);
                  }}
                  className="text-xs font-medium"
                  style={{ color: "var(--accent)" }}
                >
                  {useRecovery
                    ? mfaMethod === "email"
                      ? t("login.useEmailCode")
                      : t("login.useAuthCode")
                    : t("login.useRecoveryCode")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode("signin");
                    setMfaToken(null);
                    setMfaCode("");
                    setError(null);
                  }}
                  className="text-xs font-medium"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {t("login.backToSignIn")}
                </button>
              </div>
            </form>
          ) : mode === "password_change" ? (
            <form onSubmit={handlePasswordChangeSubmit} className="space-y-4 mb-4">
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
                  {t("login.newPasswordLabel")}
                </label>
                <PasswordInput
                  autoFocus
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t("login.newPasswordPlaceholder")}
                  required
                  className="rounded-xl py-2.5"
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
                  {t("login.confirmPasswordLabel")}
                </label>
                <PasswordInput
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t("login.confirmPasswordPlaceholder")}
                  required
                  className="rounded-xl py-2.5"
                />
              </div>

              {error && (
                <p className="text-xs px-3 py-2 rounded-lg" style={{ background: "rgba(239,68,68,0.1)", color: "#EF4444" }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-3 rounded-xl text-sm font-bold transition-all duration-200"
                style={{
                  background: loading ? "var(--bg-surface-3)" : "#3366FF",
                  color: loading ? "var(--text-secondary)" : "#FFFFFF",
                  border: "1px solid transparent",
                  boxShadow: loading ? "none" : "0 8px 32px rgba(var(--accent-rgb),0.3)",
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? t("login.settingPassword") : t("login.setNewPassword")}
              </button>

              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setMode("signin");
                    setChangeToken(null);
                    setNewPassword("");
                    setConfirmPassword("");
                    setError(null);
                  }}
                  className="text-xs font-medium"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {t("login.backToSignIn")}
                </button>
              </div>
            </form>
          ) : mode === "forgot" ? (
            <form onSubmit={handleForgotPassword} className="space-y-4 mb-4">
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
                  {t("login.emailLabel")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("login.emailPlaceholder")}
                  required
                  autoComplete="email"
                  className="w-full px-3 py-2.5 rounded-xl text-sm outline-none transition-all duration-150"
                  style={{
                    background: "var(--bg-surface-2)",
                    border: "1px solid var(--border)",
                    color: "var(--text-primary)",
                  }}
                  onFocus={inputFocus}
                  onBlur={inputBlur}
                />
              </div>

              {error && (
                <p className="text-xs px-3 py-2 rounded-lg" style={{ background: "rgba(239,68,68,0.1)", color: "#EF4444" }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-3 rounded-xl text-sm font-bold transition-all duration-200"
                style={{
                  background: loading ? "var(--bg-surface-3)" : "#3366FF",
                  color: loading ? "var(--text-secondary)" : "#FFFFFF",
                  border: "1px solid transparent",
                  boxShadow: loading ? "none" : "0 8px 32px rgba(var(--accent-rgb),0.3)",
                  opacity: loading ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    (e.currentTarget as HTMLElement).style.boxShadow = "0 12px 40px rgba(var(--accent-rgb),0.45)";
                    (e.currentTarget as HTMLElement).style.transform = "translateY(-1px)";
                  }
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = "0 8px 32px rgba(var(--accent-rgb),0.3)";
                  (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
                }}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    {t("login.sending")}
                  </>
                ) : (
                  t("login.sendResetLink")
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setMode("signin");
                    setError(null);
                  }}
                  className="text-sm transition-colors duration-150"
                  style={{ color: "var(--text-secondary)" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.color = "var(--text-primary)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.color = "var(--text-secondary)";
                  }}
                >
                  <span style={{ color: "var(--accent)", fontWeight: 600 }}>{t("login.backToSignIn")}</span>
                </button>
              </div>
            </form>
          ) : (
            <>
              {/* Auth Form */}
              <form onSubmit={handleLogin} className="space-y-4 mb-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
                    {t("login.emailLabel")}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("login.emailPlaceholder")}
                    required
                    className="w-full px-3 py-2.5 rounded-xl text-sm outline-none transition-all duration-150"
                    style={{
                      background: "var(--bg-surface-2)",
                      border: "1px solid var(--border)",
                      color: "var(--text-primary)",
                    }}
                    onFocus={inputFocus}
                    onBlur={inputBlur}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-medium" style={{ color: "var(--text-secondary)" }}>
                      {t("login.passwordLabel")}
                    </label>
                    {mode === "signin" && (
                      <button
                        type="button"
                        onClick={() => {
                          setMode("forgot");
                          setError(null);
                        }}
                        className="text-xs font-medium transition-colors duration-150"
                        style={{ color: "var(--accent)" }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.textDecoration = "underline";
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.textDecoration = "none";
                        }}
                      >
                        {t("login.forgotPassword")}
                      </button>
                    )}
                  </div>
                  <PasswordInput
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("login.passwordPlaceholder")}
                    required
                    className="rounded-xl py-2.5"
                  />
                </div>

                {error && (
                  <p className="text-xs px-3 py-2 rounded-lg" style={{ background: "rgba(239,68,68,0.1)", color: "#EF4444" }}>
                    {error}
                  </p>
                )}

                {retrySecondsLeft !== null && retrySecondsLeft > 0 && (
                  <p className="text-xs px-3 py-1 rounded-lg" style={{ background: "rgba(251,191,36,0.1)", color: "#D97706" }}>
                    {t("login.retryIn", {
                      time: `${Math.floor(retrySecondsLeft / 60)}:${String(retrySecondsLeft % 60).padStart(2, "0")}`,
                    })}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading || (retrySecondsLeft !== null && retrySecondsLeft > 0)}
                  className="w-full flex items-center justify-center gap-3 py-3 rounded-xl text-sm font-bold transition-all duration-200"
                  style={{
                    background: loading ? "var(--bg-surface-3)" : "#3366FF",
                    color: loading ? "var(--text-secondary)" : "#FFFFFF",
                    border: "1px solid transparent",
                    boxShadow: loading ? "none" : "0 8px 32px rgba(var(--accent-rgb),0.3)",
                    opacity: loading ? 0.7 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      (e.currentTarget as HTMLElement).style.boxShadow = "0 12px 40px rgba(var(--accent-rgb),0.45)";
                      (e.currentTarget as HTMLElement).style.transform = "translateY(-1px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.boxShadow = "0 8px 32px rgba(var(--accent-rgb),0.3)";
                    (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
                  }}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                      {t("login.signingIn")}
                    </>
                  ) : (
                    t("login.signIn")
                  )}
                </button>
              </form>
              
            </>
          )}

          {/* Footer note */}
          <p className="text-center text-xs mt-8" style={{ color: "var(--text-muted)" }}>
            {t("login.footer")}
          </p>
        </div>
      </div>

    </div>
  );
};

export default LoginScreen;
